import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { productThumb, productThumbSrcSet } from '../lib/cloudinary';
import { useSettings } from '../contexts/SettingsContext';

const BASE = process.env.REACT_APP_BACKEND_URL;
function resolveUrl(u) { return (!u || u.startsWith('http')) ? u : `${BASE}${u}`; }

/**
 * Single product card used across:
 *  - HomePage (FirstDropSection)
 *  - ShopPage
 *  - CollectionPage
 *  - ProductDetailPage (related)
 *
 * Props:
 *  product       — product object from API
 *  index         — position in list (0-based), used for eager/lazy loading
 *  sizes         — responsive image sizes string (optional)
 *  thumbWidth    — Cloudinary thumb width in px (default 480)
 *  showWA        — show WhatsApp hover button (default true)
 *  className     — extra class on wrapper
 */
export default function ProductCard({
  product,
  index = 0,
  sizes = '(max-width:768px) 50vw, (max-width:1200px) 33vw, 25vw',
  thumbWidth = 480,
  showWA = true,
  className = '',
}) {
  const { waLink } = useSettings();
  const isOOS = product.is_out_of_stock === true;
  const imgSrc = resolveUrl(product.image_url);

  return (
    <div className={`group ${className}`} data-testid={`product-card-${product.id}`}>
      <Link to={`/product/${product.id}`}>
        <div className="relative overflow-hidden bg-[#E8E1D8] aspect-[3/4] mb-2 md:mb-3">
          {/* Product image */}
          <img
            src={productThumb(imgSrc, thumbWidth)}
            srcSet={productThumbSrcSet(imgSrc)}
            sizes={sizes}
            alt={product.name}
            loading={index < 4 ? 'eager' : 'lazy'}
            fetchpriority={index < 4 ? 'high' : 'auto'}
            decoding="async"
            className={`w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${isOOS ? 'grayscale opacity-60' : ''}`}
            onLoad={e => { e.currentTarget.style.opacity = '1'; }}
            style={{ opacity: 0, transition: 'opacity 0.25s ease' }}
            onError={e => { e.currentTarget.style.opacity = '1'; }}
          />

          {/* ── Out of Stock overlay — brand style ── */}
          {isOOS && (
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-4 pointer-events-none">
              {/* Dark frosted strip */}
              <div className="w-full bg-[#1E1C1A]/80 backdrop-blur-[2px] py-2.5 flex flex-col items-center gap-0.5">
                <span className="text-[#F6F3EE] text-[9px] md:text-[10px] font-medium uppercase tracking-[0.25em]">
                  Out of Stock
                </span>
                <span className="text-[#C6A978] text-[8px] md:text-[9px] font-light tracking-[0.18em] uppercase">
                  This piece is gone. ✦
                </span>
              </div>
            </div>
          )}

          {/* WhatsApp hover button — hidden when OOS */}
          {showWA && !isOOS && (
            <a
              href={waLink(`Hi, I want to order ${product.name}`)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="absolute bottom-2 left-2 right-2 bg-[#1E1C1A] text-[#F6F3EE] py-2 text-[9px] md:text-[10px] tracking-[0.12em] uppercase text-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 flex items-center justify-center gap-1.5"
            >
              <MessageCircle size={10} /> Reserve
            </a>
          )}
        </div>
      </Link>

      <Link to={`/product/${product.id}`}>
        <h3 className={`font-['Cormorant_Garamond'] text-sm md:text-base font-medium tracking-tight mb-0.5 group-hover:text-[#C6A978] transition-colors leading-tight ${isOOS ? 'text-[#7A7369]' : 'text-[#1E1C1A]'}`}>
          {product.name}
        </h3>
      </Link>

      <div className="flex items-baseline gap-1.5">
        {isOOS ? (
          <span className="text-[10px] md:text-xs tracking-[0.15em] uppercase text-[#7A7369]">Sold</span>
        ) : (
          <>
            <span className="text-xs md:text-sm tracking-wide text-[#1E1C1A]">&#8377;{product.price}</span>
            {product.mrp && product.mrp > product.price && (
              <span className="text-[10px] text-[#7A7369] line-through">&#8377;{product.mrp}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
}